import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query'

import {
  closeSavingsAccount,
  createSavingsProduct,
  issuePassbook,
  openSavingsAccount,
  placeAccountHold,
  postDeposit,
  postSavingsInterest,
  postTransfer,
  postWithdrawal,
  reactivateSavingsAccount,
  releaseAccountHold,
  runDormancySweep,
  savingsKeys,
  topUpSecurityDeposit,
} from '@/api/savings-api'
import type {
  CloseSavingsAccountRequest,
  CreateSavingsProductRequest,
  IssuePassbookRequest,
  OpenSavingsAccountRequest,
  PlaceHoldRequest,
  PostInterestRequest,
  ReactivateAccountRequest,
  ReleaseHoldRequest,
  RunDormancyRequest,
  SavingsMovementRequest,
  SavingsTransferRequest,
} from '@/api/savings-types'

/**
 * Commands over the savings module.
 *
 * Every posting changes three things a screen may be showing at once — the account's balances, the
 * statement it produced a line in, and the row in whatever list the operator came from — so each
 * of these invalidates all three rather than writing a response into one cache entry. A posting
 * response carries `balanceAfterMinor`, but writing that into the account cache would replace a
 * whole record with a partial view of it, and the fields it does not carry (holds, accrued
 * interest, last transaction date) would silently go stale in the same instant.
 *
 * A transfer is the case that makes the argument concrete: it moves money between two accounts and
 * the response describes one of them. The destination is invalidated by prefix rather than
 * updated, because the client is not entitled to guess what the other side now holds.
 */
function invalidateAccount(queryClient: QueryClient, accountId: string) {
  void queryClient.invalidateQueries({ queryKey: savingsKeys.account(accountId) })
  void queryClient.invalidateQueries({ queryKey: savingsKeys.accountLists() })
}

/** Everything under `savings.account`, for a command whose effect is not confined to one account. */
function invalidateAllAccounts(queryClient: QueryClient) {
  void queryClient.invalidateQueries({ queryKey: savingsKeys.accounts() })
}

export function useOpenSavingsAccount() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: OpenSavingsAccountRequest) => openSavingsAccount(request),
    onSuccess: (account) => {
      queryClient.setQueryData(savingsKeys.account(account.id), account)
      void queryClient.invalidateQueries({ queryKey: savingsKeys.accountLists() })
      // Opening an account can satisfy a loan gate, and the prerequisites answer is the screen
      // somebody is looking at while they do it.
      void queryClient.invalidateQueries({ queryKey: savingsKeys.all })
    },
  })
}

export function usePostDeposit(accountId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: SavingsMovementRequest) => postDeposit(accountId, request),
    onSuccess: () => {
      invalidateAccount(queryClient, accountId)
    },
  })
}

/**
 * Dual control. The answer is a `DualControlResult` and never a posting, so nothing is written
 * into any cache here — when the withdrawal is queued there is no posting to write, and when it
 * executed the response still carries none of its figures.
 */
export function usePostWithdrawal(accountId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: SavingsMovementRequest) => postWithdrawal(accountId, request),
    onSuccess: () => {
      invalidateAccount(queryClient, accountId)
    },
  })
}

/** Both sides move, and only one is described by the response — see the note above. */
export function usePostTransfer(accountId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: SavingsTransferRequest) => postTransfer(accountId, request),
    onSuccess: (_posting, request) => {
      invalidateAccount(queryClient, accountId)
      void queryClient.invalidateQueries({ queryKey: savingsKeys.account(request.destinationAccountId) })
    },
  })
}

export function useTopUpSecurityDeposit(accountId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: SavingsMovementRequest) => topUpSecurityDeposit(accountId, request),
    onSuccess: () => {
      invalidateAccount(queryClient, accountId)
      // A top-up is usually being made to clear a loan prerequisite, and that answer is cached.
      void queryClient.invalidateQueries({ queryKey: savingsKeys.all })
    },
  })
}

/**
 * Places a hold — or amends the active hold that already carries this reason. The response is the
 * hold, but the figure that matters is the account's available balance, which only a refetch has.
 */
export function usePlaceAccountHold(accountId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: PlaceHoldRequest) => placeAccountHold(accountId, request),
    onSuccess: () => {
      invalidateAccount(queryClient, accountId)
    },
  })
}

export function useReleaseAccountHold(accountId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ holdId, ...request }: ReleaseHoldRequest & { holdId: string }) =>
      releaseAccountHold(holdId, request),
    onSuccess: () => {
      invalidateAccount(queryClient, accountId)
    },
  })
}

export function useReactivateSavingsAccount(accountId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: ReactivateAccountRequest) => reactivateSavingsAccount(accountId, request),
    onSuccess: () => {
      invalidateAccount(queryClient, accountId)
    },
  })
}

/** Dual control, and it pays the balance out in cash. Nothing is assumed about the outcome. */
export function useCloseSavingsAccount(accountId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: CloseSavingsAccountRequest) => closeSavingsAccount(accountId, request),
    onSuccess: () => {
      invalidateAccount(queryClient, accountId)
    },
  })
}

/** Dual control. A queued product does not exist yet, so no product list is written into. */
export function useCreateSavingsProduct() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: CreateSavingsProductRequest) => createSavingsProduct(request),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: savingsKeys.products() })
    },
  })
}

export function useIssuePassbook(customerId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: IssuePassbookRequest) => issuePassbook(customerId, request),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: savingsKeys.passbooks(customerId) })
      // A book is one of the three loan gates.
      void queryClient.invalidateQueries({ queryKey: savingsKeys.all })
    },
  })
}

/**
 * Capitalises interest across a product at a branch. It touches every account the run reached, so
 * nothing narrower than "every account" is a safe invalidation.
 */
export function usePostSavingsInterest() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: PostInterestRequest) => postSavingsInterest(request),
    onSuccess: () => {
      invalidateAllAccounts(queryClient)
    },
  })
}

/** Ages idle accounts into dormancy across a branch. Same reach, same invalidation. */
export function useRunDormancySweep() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: RunDormancyRequest) => runDormancySweep(request),
    onSuccess: () => {
      invalidateAllAccounts(queryClient)
    },
  })
}
